import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

import TipTapEditor from './Editor';
import CustomLabels from '../../../components/CustomLabels';
import { showToast } from '../../../components/ToastConfig';

import NewEventService from '../../../services/event-service';

const PHOTO_BASIC = process.env.REACT_APP_FILE_BASIC;

// Image Preview Component
const ImagePreview = ({ imageFile, imageUrl }) => {
  const [previewUrl, setPreviewUrl] = useState(imageUrl || '');

  // Create preview URL from file
  useEffect(() => {
    if (imageFile) {
      const objectUrl = URL.createObjectURL(imageFile);
      setPreviewUrl(objectUrl);
      
      // Free memory when component unmounts
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [imageFile]);

  return previewUrl ? (
    <div 
      className="w-full h-0 pb-[50%] bg-gray-100 relative rounded overflow-hidden" 
      style={{ backgroundImage: `url(${previewUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    />
  ) : (
    <div className="w-full h-0 pb-[50%] bg-gray-100 flex items-center justify-center relative rounded">
      <span className="text-gray-400 absolute top-1/2 -translate-y-1/2">預覽圖片</span>
    </div>
  );
};

// API service for uploading images
// 在 Step2.js 中修改
const uploadImageToAPI = async (file, type, eventId) => {
  try {
    // 使用傳入的 eventId 參數，而不是從狀態或參數中獲取
    const formData = new FormData();
    formData.append('file', file);
    
    let data;
    if (type === 'list') {
      data = await NewEventService.uploadListImage(eventId, formData);
    } else {
      data = await NewEventService.uploadHeaderImage(eventId, formData);
    }

    return data?.data;
  } catch (error) {
    console.error('Image upload failed:', error);
    throw error;
  }
};

// Step2Form with image uploads and rich text editors
const Step2 = ({ register, errors, watch, setValue, control, eventId }) => {
  // Loading states for image uploads
  const [isListImageUploading, setIsListImageUploading] = useState(false);
  const [isHeaderImageUploading, setIsHeaderImageUploading] = useState(false);
  const { id } = useParams();
  
  // State for file objects
  const [listImageFile, setListImageFile] = useState(null);
  const [headerImageFile, setHeaderImageFile] = useState(null);
  
  // Watch for existing image URLs (in case of form edit)
  const watchListImageUrl = watch('listImageUrl');
  const watchHeaderImageUrl = watch('headerImageUrl');
  
  // Create refs for the TipTap editors
  const introEditorRef = useRef(null);
  const aboutEditorRef = useRef(null);
  
  // Get current values for the rich text fields
  const introContent = watch('intro') || '';
  const aboutContent = watch('about') || '';

  // Handle file input changes and upload to API
// 在 Step2.js 中修改
const handleFileChange = async (e, setFileFunction, urlFieldName, setLoadingState, type) => {
  const file = e.target.files[0];
  if (!file) return;
  
  try {
    // 更新本地狀態用於預覽
    setFileFunction(file);
    
    // 設置加載狀態
    setLoadingState(true);
    
    // 獲取當前 URL 中的 ID（這比從 props 或 useParams 獲取更可靠）
    const currentPath = window.location.hash || window.location.pathname;
    const currentId = currentPath.split('/').pop(); // 從 URL 中提取 ID
    
    // 上傳到 API，傳遞當前 ID
    let uploadedImageUrl;
    if (type === 'list') {
      // uploadedImageUrl = await uploadImageToAPI(file, 'list', currentId);
      uploadedImageUrl = await uploadImageToAPI(file, 'list', eventId);
    } else {
      // uploadedImageUrl = await uploadImageToAPI(file, 'header', currentId);
      uploadedImageUrl = await uploadImageToAPI(file, 'header', eventId);
    }
    
    // 更新表單值
    if (uploadedImageUrl?.startsWith(PHOTO_BASIC)) {
      setValue(urlFieldName, uploadedImageUrl);
    } else {
      setValue(urlFieldName, `${PHOTO_BASIC}${uploadedImageUrl}`);
    }
  } catch (error) {
    showToast.error('圖片上傳失敗，請稍後再試');
    e.target.value = null;
  } finally {
    setLoadingState(false);
  }
};

  return (
    <div className="space-y-6">
      <div>
        {/* Image fields - side by side in one row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* listImageUrl field with file input and preview */}
          <div className="space-y-1">
            <label className="block text-sm font-medium">列表頁面圖片 URL</label>
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, setListImageFile, 'listImageUrl', setIsListImageUploading, 'list')}
                  className="w-full p-2 border rounded border-gray-300"
                  disabled={isListImageUploading}
                />
                {/* Hidden input to store the URL for form submission */}
                <input
                  type="hidden"
                  {...register('listImageUrl')}
                />
                {isListImageUploading && (
                  <p className="text-blue-500 text-sm mt-1">上傳中...</p>
                )}
                {errors.listImageUrl && (
                  <p className="text-red-500 text-sm">{errors.listImageUrl.message}</p>
                )}
              </div>
              <div className="w-1/3">
                <ImagePreview imageFile={listImageFile} imageUrl={watchListImageUrl} />
              </div>
            </div>
          </div>

          {/* headerImageUrl field with file input and preview */}
          <div className="space-y-1">
            <label className="block text-sm font-medium">細節頁面 Banner URL</label>
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, setHeaderImageFile, 'headerImageUrl', setIsHeaderImageUploading, 'header')}
                  className="w-full p-2 border rounded border-gray-300"
                  disabled={isHeaderImageUploading}
                />
                {/* Hidden input to store the URL for form submission */}
                <input
                  type="hidden"
                  {...register('headerImageUrl')}
                />
                {isHeaderImageUploading && (
                  <p className="text-blue-500 text-sm mt-1">上傳中...</p>
                )}
                {errors.headerImageUrl && (
                  <p className="text-red-500 text-sm">{errors.headerImageUrl.message}</p>
                )}
              </div>
              <div className="w-1/3">
                <ImagePreview imageFile={headerImageFile} imageUrl={watchHeaderImageUrl} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        {/* Rich text editors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* intro field with TipTap editor */}
          <div className="space-y-1">
            <label className="block text-sm font-medium mb-1">介紹</label>
            <TipTapEditor
              ref={introEditorRef}
              value={introContent}
              onChange={(html) => setValue('intro', html)}
              disabled={false}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>

          {/* about field with TipTap editor */}
          <div className="space-y-1">
            <label className="block text-sm font-medium mb-1">關於</label>
            <TipTapEditor
              ref={aboutEditorRef}
              value={aboutContent}
              onChange={(html) => setValue('about', html)}
              disabled={false}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2;